import {createContext, useState} from "react";
import { calculateShipping, countPhysicalItemsBySize } from "../utils/calculateShipping";

export const CartContext = createContext(undefined);

export const CartProvider = ({ children }) => {
    const [products, setProducts] = useState({});
    const [originalDonation, setOriginalDonation] = useState(undefined);
    const [transactionFee, setTransactionFee] = useState(undefined);
    const [donationSettings, updateDonationSettings] = useState(undefined);
    const [custref, setCustRef] = useState(undefined);
    const [isRecurring, setIsRecurring] = useState(false);
    const [shippingConfig, setShippingConfig] = useState(undefined);

    const parseNumber = function(amount) {
        if (amount == null || amount === "0") {
            return 0
        }
        const parsed = parseFloat(amount.toString().replaceAll(",", ""))
        return isNaN(parsed) ? 0 : parsed
    }

    const calculateShippingCost = function() {
        const counts = countPhysicalItemsBySize(products);
        return calculateShipping(counts, shippingConfig);
    }

    const hasPhysicalItems = function() {
        const c = countPhysicalItemsBySize(products);
        return (c.small + c.medium + c.large) > 0;
    }

    const calculateSubtotal = function() {
        let dollars = 0
        for (let key of Object.keys(products)) {
            dollars += parseNumber(products[key].price)
        }

        return dollars;
    }

    // No-op kept for backward compatibility — total is now derived, not stored as state
    const recalculateAndSetTotal = function() {}

    const roundUpToNearestCent = function(amount) {
        return Math.ceil(amount * 100) / 100
    }

    const calculateProcessingFee = function(rate) {
        const donationAmount = originalDonation ? parseNumber(originalDonation.price) : 0;
        return roundUpToNearestCent((parseNumber(rate) / 100) *
            (calculateTotalWithoutDonation() + donationAmount));
    }

    // Keep the donation portion of the cart inclusive of optional fee coverage.
    const calculateCoveredCost = function(rate) {
        return (originalDonation ? parseNumber(originalDonation.price) : 0) + calculateProcessingFee(rate);
    }

    const setDonationSettings = function(newDonationSettings) {
        updateDonationSettings(newDonationSettings && {
            ...newDonationSettings,
            isSelected: newDonationSettings.isSelected ?? !!newDonationSettings.selectedByDefault,
        });
    }

    const calculateTotalWithoutDonation = function() {
        let productTotal = Object.keys(products)
            .filter(key => key !== "form-fee")
            .reduce((sum, key) => sum + parseNumber(products[key].price), 0);

        let dollars = productTotal;

        if (products["form-fee"]) {
            dollars += parseNumber(products["form-fee"].price);
        }

        if (transactionFee && transactionFee.type === "amount" && transactionFee.applied_per === "transaction") {
            dollars += parseNumber(transactionFee.amount);
        }

        if (transactionFee && transactionFee.type === "percent" && transactionFee.applied_per === "form") {
            dollars += transactionFee.amount * productTotal;
        }

        dollars += calculateShippingCost();

        return dollars;
    }

    const calculateTotal = function() {
        var dollars = calculateTotalWithoutDonation()

        if (donation) {
            dollars += parseNumber(donation.price)
        }

        return dollars;
    }

    const removeProduct = function(id) {
        delete products[id]
    }

    const removeDonation = function() {
        setOriginalDonation(undefined)
    }

    const updateDonation = function(id, name, price) {
        const newDonation = {
            id: id,
            name: name,
            price: parseNumber(price)
        };
        setOriginalDonation(newDonation);
    }

    const updateProduct = (id, name, quantity, price, quantityAvailable, amountSold, productActivityInProgressText, isPhysical = false, shippingSize = null) => {
        setProducts((prevProducts) => {
            const newProduct = {
                name,
                quantity,
                price: parseFloat(price) * quantity,
                quantityAvailable,
                amountSold,
                productActivityInProgressText,
                isPhysical,
                shippingSize,
            };

            const { ["form-fee"]: formFee, ...otherProducts } = prevProducts;

            let updatedProducts = {
                ...otherProducts,
                [id.toString()]: newProduct,
            };

            if (id === "form-fee" || formFee) {
                const finalFormFee = id === "form-fee" ? newProduct : formFee;
                delete updatedProducts["form-fee"];
                updatedProducts["form-fee"] = finalFormFee;
            }

            return updatedProducts;
        });
    };

    const donation = donationSettings?.isSelected && parseNumber(donationSettings.rate)
        ? {
            id: originalDonation ? originalDonation.id : 'covered_cost',
            name: originalDonation ? originalDonation.name : 'Processing Fee Cover',
            price: calculateCoveredCost(donationSettings.rate),
        }
        : originalDonation;

    // Derive total, subtotal, and shipping from current state on every render — always correct
    const total = calculateTotal();
    const subtotal = calculateSubtotal();
    const shipping = calculateShippingCost();

    return (
        <CartContext.Provider
            value={{
                donation,
                updateDonation,
                updateProduct,
                removeDonation,
                removeProduct,
                products,
                transactionFee,
                setTransactionFee,
                total,
                calculateTotal,
                subtotal,
                calculateSubtotal,
                recalculateAndSetTotal,
                custref,
                setCustRef,
                setDonationSettings,
                donationSettings,
                calculateCoveredCost,
                calculateProcessingFee,
                isRecurring,
                setIsRecurring,
                shipping,
                shippingConfig,
                setShippingConfig,
                hasPhysicalItems,
                calculateShippingCost,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};
